/**
 * 入力ページ(新しい申請など)。左にフォーム、右に要約(常時見える)、下に固定フッター。
 *
 * なぜ新設か: `DetailPage` は「読む」ページでタブを持ち、`PageHeader + DataTable` は一覧。
 * 入力ページは**段階(手順)+ 要約の併置 + 戻る/次への固定フッター**が要り、どちらにも
 * 無理に載らない。Modal(RequestForm)は 800px で 4 段階を収められず縦に長くなっていた。
 */
import { CloseOutlined } from "@ant-design/icons";
import { Button } from "antd";
import type { ReactNode } from "react";

export type FormStep = { key: string; label: string };

export function FormPage({
  title,
  description,
  steps,
  current,
  onStep,
  onClose,
  closeLabel = "閉じる",
  aside,
  footer,
  children,
}: {
  title: string;
  description?: ReactNode;
  /** 手順(上に横並び)。省略時は出さない */
  steps?: FormStep[];
  /** いまの手順の key */
  current?: string;
  /** 済んだ手順を押して戻る(未達の手順は押せない) */
  onStep?: (key: string) => void;
  onClose: () => void;
  closeLabel?: string;
  /** 右カラム(要約・承認ルート)。1100px 未満では下に落ちる */
  aside?: ReactNode;
  /** 下の固定帯(戻る / 次へ) */
  footer: ReactNode;
  children: ReactNode;
}) {
  const idx = steps?.findIndex((s) => s.key === current) ?? -1;
  return (
    <div className="ui-form">
      <div className="ui-form-head">
        <div className="ui-form-head-top">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1>{title}</h1>
            {description && <p>{description}</p>}
          </div>
          <Button type="text" icon={<CloseOutlined />} onClick={onClose} aria-label={closeLabel}>
            {closeLabel}
          </Button>
        </div>
        {steps && steps.length > 0 && (
          <ol className="ui-form-stepper" aria-label="手順">
            {steps.map((s, i) => {
              const state = i < idx ? "is-done" : i === idx ? "is-current" : "";
              const inner = (
                <>
                  <span aria-hidden>{i < idx ? "✓" : i + 1}</span>
                  <span>{s.label}</span>
                </>
              );
              return (
                <li key={s.key} className={state} aria-current={i === idx ? "step" : undefined}>
                  {i < idx && onStep ? (
                    <button type="button" onClick={() => onStep(s.key)}>
                      {inner}
                    </button>
                  ) : (
                    inner
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
      <div className="ui-form-body">
        <div className="ui-form-main">{children}</div>
        {aside && <aside className="ui-form-aside">{aside}</aside>}
      </div>
      <div className="ui-form-footer">{footer}</div>
    </div>
  );
}
